import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const linkClass =
  "font-semibold text-navy underline decoration-gold/80 underline-offset-[5px] transition-colors hover:decoration-gold";

const GROUPS = [
  {
    label: "Platform",
    items: [
      {
        n: "01",
        q: "What is SimFinance?",
        a: "SimFinance is an intelligent digital financial ecosystem that combines a financial marketplace (SimMarkets), digital trust verification (SimVerify) and debt recovery (SimCollect) into one integrated platform.",
      },
      {
        n: "02",
        q: "Is SimFinance a bank?",
        a: "No. SimFinance is a financial technology company and ecosystem that connects consumers with verified financial institutions and provides technology solutions to the financial services industry.",
      },
      {
        n: "03",
        q: "Which financial institutions can join?",
        a: "Banks, insurance companies, savings & loans, fintechs, microfinance institutions, pension providers, investment firms, mortgage providers and many other financial service organizations.",
      },
    ],
  },
  {
    label: "Products",
    items: [
      {
        n: "04",
        q: "What is SimMarkets?",
        a: "SimMarkets is the marketplace that educates consumers, compares financial products using AI, recommends suitable options and delivers qualified customer leads.",
      },
      {
        n: "05",
        q: "How does SimVerify reduce fraud?",
        a: "It verifies borrowers, vehicles, devices and SIM behaviour while generating AI-powered Trust Scores that help institutions make smarter lending decisions.",
      },
      {
        n: "06",
        q: "What is SimCollect?",
        a: "SimCollect is an intelligent debt recovery platform that automates customer engagement, repayments and collections across multiple digital channels.",
      },
    ],
  },
  {
    label: "Trust & access",
    items: [
      {
        n: "07",
        q: "Is customer information secure?",
        a: "Yes. SimFinance is designed with strong data protection principles and aligns with Ghana's Data Protection Act, consumer protection requirements and electronic communication regulations.",
      },
      {
        n: "08",
        q: "Can developers integrate with SimFinance?",
        a: "Yes. SimFinance provides secure API integration capabilities for financial institutions and technology partners, particularly through SimVerify, SimCollect and the broader ecosystem.",
      },
      {
        n: "09",
        q: "How do I become a partner?",
        a: (
          <>
            Simply complete the{" "}
            <a href="#partner" className={linkClass}>
              Partnership Request form
            </a>{" "}
            or contact our Business Development team to begin the onboarding process.
          </>
        ),
      },
      {
        n: "10",
        q: "How do I request a product demonstration?",
        a: (
          <>
            Click the{" "}
            <a href="#contact" className={linkClass}>
              Request a Demo
            </a>{" "}
            button anywhere on the website, complete the short form, and one of our product specialists
            will schedule a personalized demonstration.
          </>
        ),
      },
    ],
  },
];

function Faqs() {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState("01");
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  const toggle = (n) => {
    setOpen((current) => (current === n ? "" : n));
  };

  const jumpTo = (n) => {
    setOpen(n);
    window.requestAnimationFrame(() => {
      document.getElementById(`faq-button-${n}`)?.focus({ preventScroll: true });
      document.getElementById(`faq-item-${n}`)?.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "nearest",
      });
    });
  };

  return (
    <section
      id="faqs"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="faqs-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(229,184,11,0.12),transparent_55%),radial-gradient(50%_40%_at_0%_80%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto grid max-w-8xl items-start gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16 lg:px-8 lg:py-28">
        <motion.header
          className="lg:sticky lg:top-28"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Frequently asked questions
          </p>
          <h2
            id="faqs-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Clear answers
            <span className="mt-1 block">to common questions</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-navy/55 sm:text-lg">
            The ecosystem, the products, and how institutions and partners get started.
          </p>

          <ul className="mt-8 flex flex-wrap gap-2" aria-label="Question topics">
            {GROUPS.map((group) => (
              <li key={group.label}>
                <button
                  type="button"
                  onClick={() => jumpTo(group.items[0].n)}
                  className="border border-navy/10 bg-[#f3f7f5] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70 transition-colors hover:border-gold hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal"
                >
                  {group.label}
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-10 overflow-hidden border border-navy/10 bg-white">
            <div className="h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
            <div className="px-5 py-5 sm:px-6">
              <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-navy/30">10 / 10</p>
              <p className="mt-2 text-sm font-semibold text-navy">Still deciding?</p>
              <p className="mt-1 text-sm leading-relaxed text-navy/50">
                Talk to the team or explore a partnership.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href="#contact"
                  className="inline-flex min-h-11 items-center rounded-full bg-gold px-5 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2"
                >
                  Request a Demo
                </a>
                <a
                  href="#partner"
                  className="inline-flex min-h-11 items-center rounded-full border border-navy/15 px-5 text-sm font-semibold text-navy transition hover:border-navy/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2"
                >
                  Become a Partner
                </a>
              </div>
            </div>
          </div>
        </motion.header>

        <motion.div
          className="relative"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <span className="pointer-events-none absolute -left-2 -top-2 h-10 w-10 border-l border-t border-gold" />
          <span className="pointer-events-none absolute -right-2 -top-2 h-10 w-10 border-r border-t border-navy/20" />
          <span className="pointer-events-none absolute -bottom-2 -left-2 h-10 w-10 border-b border-l border-navy/20" />
          <span className="pointer-events-none absolute -bottom-2 -right-2 h-10 w-10 border-b border-r border-gold" />

          <div className="relative overflow-hidden border border-navy/10 bg-white">
            {GROUPS.map((group, groupIndex) => (
              <div key={group.label} className={groupIndex === 0 ? "" : "border-t border-navy/10"}>
                <p className="border-b border-navy/10 bg-[#f3f7f5] px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/40 sm:px-6">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isOpen = open === item.n;
                  const panelId = `faq-panel-${item.n}`;
                  const buttonId = `faq-button-${item.n}`;

                  return (
                    <div
                      key={item.n}
                      id={`faq-item-${item.n}`}
                      className="border-b border-navy/10 last:border-b-0"
                    >
                      <h3>
                        <button
                          type="button"
                          id={buttonId}
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() => toggle(item.n)}
                          className={`flex w-full items-start gap-4 px-5 py-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal sm:gap-5 sm:px-6 ${
                            isOpen ? "bg-[#f3f7f5]" : "bg-white hover:bg-[#f3f7f5]"
                          }`}
                        >
                          <span
                            className={`relative mt-1 block h-8 w-[2px] shrink-0 ${
                              isOpen ? "bg-gold" : "bg-navy/10"
                            }`}
                            aria-hidden="true"
                          />
                          <span
                            className={`mt-0.5 shrink-0 font-mono text-[11px] font-semibold tracking-[0.16em] ${
                              isOpen ? "text-gold" : "text-navy/30"
                            }`}
                          >
                            {item.n}
                          </span>
                          <span className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-tight text-navy sm:text-lg">
                            {item.q}
                          </span>
                          <span
                            aria-hidden="true"
                            className={`relative mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border ${
                              isOpen
                                ? "border-gold bg-gold text-navy"
                                : "border-navy/15 bg-white text-navy"
                            }`}
                          >
                            <span className="absolute h-px w-3 bg-current" />
                            <span
                              className={`absolute h-3 w-px bg-current transition-transform ${
                                isOpen ? "scale-y-0" : "scale-y-100"
                              }`}
                            />
                          </span>
                        </button>
                      </h3>
                      <motion.div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        initial={false}
                        animate={isOpen ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
                        transition={reduce ? { duration: 0 } : { duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p
                          className={`px-5 pb-6 pl-[4.75rem] text-sm leading-relaxed text-navy/60 sm:px-6 sm:pl-[6.25rem] sm:text-base ${
                            isOpen ? "bg-[#f3f7f5]" : ""
                          }`}
                        >
                          {item.a}
                        </p>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Faqs;
